import avatar from "assets/img/avatars/avatar11.png";
import banner from "assets/img/profile/banner.png";
import Card from "components/card";
import { useAuthContext } from "hooks/AuthContext";
import { backendOrigin } from "../../../../api";
import React, { useRef, useState } from "react";
import { updateCurrentUser, deleteCurrentUserPfp } from "services/users";
import { useToast } from "components/Toast";

const getPfpUrl = (pfp?: string) => {
  if (!pfp) return avatar;
  if (pfp.startsWith("http") || pfp.startsWith("https") || pfp.startsWith("/"))
    return pfp.startsWith("/") ? `${backendOrigin}${pfp}` : pfp;
  return `${backendOrigin}/storage/${pfp}`;
};

const Banner = () => {
  const { user, setUser } = useAuthContext();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const pfpUrl = getPfpUrl(user?.pfp);
  const displayName =
    user?.name ||
    `${user?.firstname || ""} ${user?.lastname || ""}`.trim() ||
    "User";
  const role = user?.role || "Member";

  return (
    <Card extra={"items-center w-full h-full p-[16px] bg-cover"}>
      {/* Background and profile */}
      <div
        className="relative mt-1 flex h-32 w-full justify-center rounded-xl bg-cover"
        style={{ backgroundImage: `url(${banner})` }}
      >
        <div className="absolute -bottom-12 flex h-[87px] w-[87px] items-center justify-center rounded-full border-[4px] border-white bg-pink-400 dark:!border-navy-700">
          <img
            className="h-full w-full rounded-full"
            src={pfpUrl}
            alt={displayName + " avatar"}
          />
        </div>
      </div>

      {/* Name and position */}
      <div className="mt-16 flex flex-col items-center">
        <h4 className="text-xl font-bold text-navy-700 dark:text-white">
          {displayName}
        </h4>
        <p className="text-base font-normal text-gray-600">{role}</p>
        <div className="mt-2 flex items-center gap-2">
          <button
            className="text-sm text-brand-500"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Change photo"}
          </button>
          {user?.pfp && (
            <button
              className="text-sm text-red-500"
              onClick={async () => {
                try {
                  setUploading(true);
                  await deleteCurrentUserPfp();
                  if (setUser) setUser({ ...(user as any), pfp: null });
                  toast.showSuccess?.("Profile photo removed");
                } catch (err) {
                  console.error(err);
                  toast.showError?.("Failed to remove profile photo");
                } finally {
                  setUploading(false);
                }
              }}
            >
              Remove
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              if (!e.target.files || !e.target.files[0]) return;
              const file = e.target.files[0];
              const formData = new FormData();
              formData.append("pfp", file);
              try {
                setUploading(true);
                const updated = await updateCurrentUser(formData);
                if (setUser) setUser(updated);
                toast.showSuccess?.("Profile photo updated");
              } catch (err) {
                console.error(err);
                toast.showError?.("Failed to upload profile photo");
              } finally {
                setUploading(false);
              }
            }}
          />
        </div>
      </div>

      {/* Post followers */}
      <div className="mb-3 mt-6 flex gap-4 md:!gap-14">
        <div className="flex flex-col items-center justify-center">
          <p className="text-2xl font-bold text-navy-700 dark:text-white">17</p>
          <p className="text-sm font-normal text-gray-600">Posts</p>
        </div>
        <div className="flex flex-col items-center justify-center">
          <p className="text-2xl font-bold text-navy-700 dark:text-white">
            9.7K
          </p>
          <p className="text-sm font-normal text-gray-600">Followers</p>
        </div>
        <div className="flex flex-col items-center justify-center">
          <p className="text-2xl font-bold text-navy-700 dark:text-white">
            434
          </p>
          <p className="text-sm font-normal text-gray-600">Following</p>
        </div>
      </div>
    </Card>
  );
};

export default Banner;
